import { bridgeGaEventToDatadog } from '@services/datadog';

import gtmConfig from '@config/google-tag-manager.json';

const GTM_CONFIG = gtmConfig;
const DATA_LAYER_VARIABLE = 'dataLayer';
const DUPLICATE_WINDOW_MS = 1500;

type TrackEventPayload = {
  event?: string;
  category?: string | string[];
  action?: string;
  label?: string;
  value?: string;
  noninteraction?: boolean;
  ecommerce?: string;
  eventCallback?: string;
  eventTimeout?: string;
  [key: string]: unknown;
};

const win = (): Record<string, unknown> => window as unknown as Record<string, unknown>;

const injectGtmScript = (id: string): void => {
  const GTM_FLAG = '__GTM_INITIALIZED__';
  const alreadyInserted = !!document.querySelector(`script[src*="googletagmanager.com/gtm.js?id=${id}"]`);
  if (win()[GTM_FLAG] || alreadyInserted) return;

  const layer = (win()[DATA_LAYER_VARIABLE] as unknown[] | undefined) ?? [];
  win()[DATA_LAYER_VARIABLE] = layer;
  layer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });

  const firstScript = document.getElementsByTagName('script')[0];
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${id}`;
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    document.head.appendChild(script);
  }

  win()[GTM_FLAG] = true;
};

const createGoogleTagManagerService = (options: { id: string; enabled: boolean }) => {
  const dataLayer = (): unknown[] => {
    const existing = win()[DATA_LAYER_VARIABLE] as unknown[] | undefined;
    if (existing) return existing;

    const layer: unknown[] = [];
    win()[DATA_LAYER_VARIABLE] = layer;
    return layer;
  };

  let lastViewSig: string | undefined;
  let lastViewTs = 0;
  let lastEventSig: string | undefined;
  let lastEventTs = 0;

  const trackView = (name: string | null | undefined, path: string, status: number): void => {
    if (!options.enabled) return;

    const payload = {
      event: 'content-view',
      'content-name': name,
      'content-path': path,
      'http-status': status,
    };
    const sig = `${payload.event}|${payload['content-name']}|${payload['content-path']}|${payload['http-status']}`;
    const now = Date.now();
    if (lastViewSig === sig && now - lastViewTs < DUPLICATE_WINDOW_MS) return;
    lastViewSig = sig;
    lastViewTs = now;

    dataLayer().push(payload);
    bridgeGaEventToDatadog('content-view', {
      'content-name': payload['content-name'],
      'content-path': payload['content-path'],
      'http-status': payload['http-status'],
    });
  };

  const trackEvent = ({
    event,
    category,
    action,
    label,
    value,
    noninteraction = false,
    ecommerce,
    eventCallback,
    eventTimeout,
    ...rest
  }: TrackEventPayload = {}): void => {
    if (!options.enabled) return;

    const payload: Record<string, unknown> = {
      event: event || 'interaction',
      target: category,
      action,
      'target-properties': label,
      value,
      'interaction-type': noninteraction,
      ecommerce,
      ...(eventCallback && { eventCallback }),
      eventTimeout,
      ...rest,
    };

    let sig: string;
    try {
      sig = JSON.stringify(payload);
    } catch {
      sig = `${payload.event}|${category}|${action}|${label}|${value}`;
    }
    const now = Date.now();
    if (lastEventSig === sig && now - lastEventTs < DUPLICATE_WINDOW_MS) return;
    lastEventSig = sig;
    lastEventTs = now;

    dataLayer().push(payload);
    bridgeGaEventToDatadog(String(payload.event), {
      target: payload.target,
      action: payload.action,
      'target-properties': payload['target-properties'],
      value: payload.value,
    });
  };

  const initialize = (): void => {
    if (typeof window !== 'undefined' && options.enabled) {
      injectGtmScript(options.id);
    }
  };

  return { initialize, trackView, trackEvent };
};

const analyticsService = createGoogleTagManagerService(GTM_CONFIG);

export { analyticsService, createGoogleTagManagerService };
